import { EngineLogo } from "./engine-logo.js";
import { engineMeta } from "./engines.js";
import { FilterPill } from "./filter-pill.js";
import { Check } from "./icons.js";
import { useCapturedEngines } from "./use-captured-engines.js";

/**
 * Which engine's answers to count.
 *
 * It offers the engines this server asks (`useCapturedEngines`), and nothing
 * while that list loads: a filter for an engine nobody asks only ever returns
 * nothing, which reads as broken data (AGENTS.md §1). An engine already chosen
 * in the URL keeps its name, from the table, so a pasted link still reads.
 *
 * "All engines" is not a value the API takes; it is the absence of the filter,
 * which is why `null` rather than a magic string. A sentinel like `"all"` is how
 * a filter ends up sending `engine=all` to a server that has never heard of it.
 */

export function EnginePill({
  engine,
  onChange,
}: {
  /** Null means every engine — the filter is off, not set to a wildcard. */
  readonly engine: string | null;
  readonly onChange: (next: string | null) => void;
}) {
  const engines = useCapturedEngines() ?? [];
  const chosen = engine === null ? undefined : engineMeta(engine);
  // Every engine counted: their marks in a stack, and how many there are.
  const all =
    engines.length === 0 ? "All engines" : `All ${String(engines.length)} engines`;

  return (
    <FilterPill
      label="Engine"
      value={chosen?.label ?? all}
      icon={
        chosen !== undefined ? (
          <EngineLogo engine={chosen.id} />
        ) : engines.length === 0 ? undefined : (
          <span className="pill-stack" aria-hidden="true">
            {engines.map((entry) => (
              <EngineLogo key={entry.id} engine={entry.id} />
            ))}
          </span>
        )
      }
    >
      {(close) => (
        <ul className="optionlist">
          <li>
            <button
              type="button"
              className={engine === null ? "option option-on" : "option"}
              onClick={() => {
                onChange(null);
                close();
              }}
            >
              <span className="option-name">All engines</span>
              {engine === null ? <Tick /> : null}
            </button>
          </li>
          {engines.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                className={engine === entry.id ? "option option-on" : "option"}
                onClick={() => {
                  onChange(entry.id);
                  close();
                }}
              >
                <EngineLogo engine={entry.id} />
                <span className="option-name">{entry.label}</span>
                {engine === entry.id ? <Tick /> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </FilterPill>
  );
}

/** The chosen mark. Inline SVG, so it inherits colour and never renders as tofu. */
function Tick() {
  return <Check className="option-tick" aria-hidden="true" />;
}
